import React, { useState, useEffect } from 'react';  import { Calendar, Clock, Target, Zap, TrendingUp, Users, CheckCircle2, AlertCircle, ArrowRight, Sparkles, Play, PenLine, Lightbulb, FileText, Wand2 } from 'lucide-react';
  
  export default function ReactStaging2() {

    const criticalPathTasks = [
      {
        title: "Strategic Profile Deep Dive",
        duration: "30 min",
        why: "Unlocks better AI help for everything",
        impact: "Saves 3+ hours on other tasks",
        icon: Target,
        color: "magenta"
      },
      {
        title: "Common App Essay Draft",
        duration: "90 min",
        why: "Blocks all applications, must do first",
        detail: 'Using: "Moments That Matter" template',
        icon: PenLine,
        color: "teal"
      },
      {
        title: "Duke 'Why Us' Brainstorm",
        duration: "30 min",
        why: "Due in 7 days - time sensitive",
        detail: "We'll use AI to help draft it",
        icon: Lightbulb,
        color: "coral"
      }
    ];
  
    const totalTime = criticalPathTasks.reduce((sum, task) => {
      const mins = parseInt(task.duration);
      return sum + mins;
    }
    , 0);

    const [timeRemaining, setTimeRemaining] = useState({
      days: 13,
      hours: 17,
      minutes: 42,
      seconds: 18
    });
  
    const [essayFlowType, setEssayFlowType] = useState('supplemental'); // 'supplemental' or 'personal'
  
    useEffect(() => {
      const timer = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev.seconds > 0) {
            return { ...prev, seconds: prev.seconds - 1 };
          } else if (prev.minutes > 0) {
            return { ...prev, minutes: prev.minutes - 1, seconds: 59 };
          } else if (prev.hours > 0) {
            return { ...prev, hours: prev.hours - 1, minutes: 59, seconds: 59 };
          }
          return prev;
        });
      }, 1000);
  
      return () => clearInterval(timer);
    }, []);
  
  

    return (
      <div className="min-h-screen bg-stone-50 font-['Reddit_Sans']">

 {/* Header with Countdown */}
 <div className="bg-slate-900 text-white py-6 px-8 border-b-2 border-slate-800">
 <div className="max-w-7xl mx-auto">
   <div className="flex items-center justify-between">
     <div>
       <h1 className="text-2xl font-bold mb-1">College Journey</h1>
       <div className="text-sm text-stone-400">Wednesday, October 29, 2025</div>
     </div>
     
     {/* Countdown Timer */}
     <div className="text-right">
       <div className="text-xs text-stone-400 uppercase tracking-wide mb-2">Northwestern ED Deadline</div>
       <div className="flex items-center gap-3 text-2xl font-bold tabular-nums">
         <div className="flex flex-col items-center">
           <span>{timeRemaining.days}</span>
           <span className="text-xs text-stone-400 font-normal">days</span>
         </div>
         <span className="text-stone-600">:</span>
         <div className="flex flex-col items-center">
           <span>{String(timeRemaining.hours).padStart(2, '0')}</span>
           <span className="text-xs text-stone-400 font-normal">hrs</span>
         </div>
         <span className="text-stone-600">:</span>
         <div className="flex flex-col items-center">
           <span>{String(timeRemaining.minutes).padStart(2, '0')}</span>
           <span className="text-xs text-stone-400 font-normal">min</span>
         </div>
         <span className="text-stone-600">:</span>
         <div className="flex flex-col items-center">
           <span>{String(timeRemaining.seconds).padStart(2, '0')}</span>
           <span className="text-xs text-stone-400 font-normal">sec</span>
         </div>
       </div>
     </div>
   </div>
 </div>
</div>
  
 {/* Critical Path Sprint - Full width prominent placement */}
 <div className="max-w-7xl mx-auto my-2 mb-8 bg-white border-2 border-slate-900 rounded-xl shadow-lg overflow-hidden">
          {/* Sprint Header */}
          <div className="bg-slate-900 px-8 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 rounded-full bg-gold-400"></div>
              <h2 className="text-lg font-bold text-white">Today's Critical Path</h2>
            </div>
            <div className="flex items-center gap-4 text-white text-sm">
              <span className="flex items-center gap-2">
                <span className="text-stone-400">3 tasks</span>
                <span className="text-stone-600">•</span>
                <span className="text-stone-400">{Math.floor(totalTime / 60)}h {totalTime % 60}m</span>
                <span className="text-stone-600">•</span>
                <span className="font-medium">Gets you 60% done</span>
              </span>
            </div>
          </div>

          {/* Sprint Tasks */}
          <div className="p-8 space-y-4">
            {criticalPathTasks.map((task, index) => {
              const Icon = task.icon;
              return (
                <div 
                  key={index}
                  className="bg-white border border-slate-300 rounded-lg p-6 hover:border-slate-900 transition-all group"
                >
                  <div className="flex items-start gap-4">
                    {/* Task number + icon */}
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <div className="w-8 h-8 rounded-full bg-slate-900 text-white flex items-center justify-center text-sm font-bold">
                        {index + 1}
                      </div>
                      <div className={`p-2 bg-${task.color}-100 rounded-lg border border-${task.color}-300`}>
                        <Icon className={`w-5 h-5 text-${task.color}-700`} />
                      </div>
                    </div>

                    {/* Task content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-base font-bold text-slate-900">{task.title}</h3>
                        <span className="text-sm text-slate-600 font-medium flex items-center gap-1 flex-shrink-0">
                          <Clock className="w-4 h-4" />
                          {task.duration}
                        </span>
                      </div>
                      
                      <div className="space-y-1.5">
                        <div className="flex items-start gap-2">
                          <Zap className="w-4 h-4 text-gold-600 flex-shrink-0 mt-0.5" />
                          <p className="text-sm text-slate-700">
                            <span className="font-medium text-slate-900">Why:</span> {task.why}
                          </p>
                        </div>
                        {task.impact && (
                          <div className="flex items-start gap-2">
                            <ArrowRight className="w-4 h-4 text-teal-600 flex-shrink-0 mt-0.5" />
                            <p className="text-sm text-teal-700 font-medium">{task.impact}</p>
                          </div>
                        )}
                        {task.detail && (
                          <div className="flex items-start gap-2">
                            <ArrowRight className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                            <p className="text-sm text-slate-600">{task.detail}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Sprint Action */}
          <div className="px-8 pb-8">
            <button className="w-full bg-slate-900 text-white border-2 border-slate-900 rounded-lg px-6 py-4 font-bold hover:bg-slate-800 transition-all shadow-sm flex items-center justify-center gap-2 text-base">
              <Play className="w-5 h-5" />
              Start Today's Sprint
            </button>
          </div>
        
</div>
 {/* Two Column Layout */}
 <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Left Column - This Week */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Active Crisis Management */}
            <div className="bg-white border-2 border-slate-900 rounded-lg p-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-2 h-2 rounded-full bg-coral-500"></div>
                <h3 className="font-bold text-slate-900">Managing Your Week</h3>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-start gap-3 p-3 bg-stone-50 rounded-lg border border-stone-200">
                  <div className="w-1.5 h-1.5 rounded-full bg-teal-500 mt-2"></div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-slate-900">Request rec from Ms. Chen</div>
                    <div className="text-xs text-coral-600 font-medium">6d overdue • Teachers need 4 weeks notice</div>
                    <p className="text-xs text-stone-600 mt-1">Draft email ready highlighting your AP Chem project</p>
                  </div>
                  <button className="text-xs font-medium text-teal-600 border border-teal-600 px-3 py-1.5 rounded hover:bg-teal-50 whitespace-nowrap">
                    Review draft
                  </button>
                </div>
  
                <div className="flex items-start gap-3 p-3 bg-stone-50 rounded-lg border border-stone-200">
                  <div className="w-1.5 h-1.5 rounded-full bg-teal-500 mt-2"></div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-slate-900">Submit SAT scores to Stanford</div>
                    <div className="text-xs text-gold-700 font-medium">Due Oct 15 • Tomorrow</div>
                    <p className="text-xs text-stone-600 mt-1">~2 min • Account linked, ready to send</p>
                  </div>
                  <button className="text-xs font-medium text-gold-600 border border-gold-600 px-3 py-1.5 rounded hover:bg-gold-50 whitespace-nowrap">
                    Send now
                  </button>
                </div>
  
                <div className="flex items-start gap-3 p-3 bg-stone-50 rounded-lg border border-stone-200">
                  <div className="w-1.5 h-1.5 rounded-full bg-teal-500 mt-2"></div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-slate-900">Finalize Northwestern "Why Northwestern?" supplement</div>
                    <div className="text-xs text-stone-600">~45 min left • Due in 14 days</div>
                    <p className="text-xs text-stone-600 mt-1">Draft nearly complete, needs final polish</p>
                  </div>
                  <button className="text-xs font-medium text-slate-700 border border-slate-300 px-3 py-1.5 rounded hover:bg-stone-100 whitespace-nowrap">
                    Continue
                  </button>
                </div>
              </div>
  
              <button className="w-full mt-4 text-sm text-stone-600 hover:text-slate-900 font-medium">
                Show 12 more ready tasks →
              </button>
            </div>
  
            {/* Application Progress */}
            <div className="bg-white border border-stone-300 rounded-lg p-6">
              <h3 className="font-bold text-slate-900 mb-4">Early Decision & Early Action</h3>
              <div className="text-xs text-coral-600 font-medium mb-4">All due November 6, 2025</div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="border border-stone-200 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="w-2 h-2 rounded-full bg-coral-500 mb-2"></div>
                      <div className="font-medium text-slate-900 text-sm">Northwestern</div>
                      <div className="text-xs text-stone-600">Early Decision</div>
                    </div>
                  </div>
                  <div className="text-xs font-medium text-coral-600 mt-3">Needs work today</div>
                </div>
  
                <div className="border border-stone-200 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="w-2 h-2 rounded-full bg-gold-500 mb-2"></div>
                      <div className="font-medium text-slate-900 text-sm">Boston College</div>
                      <div className="text-xs text-stone-600">Early Action</div>
                    </div>
                  </div>
                  <div className="text-xs text-stone-600 mt-3">2 items left</div>
                </div>
  
                <div className="border border-stone-200 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="w-2 h-2 rounded-full bg-gold-500 mb-2"></div>
                      <div className="font-medium text-slate-900 text-sm">U Michigan</div>
                      <div className="text-xs text-stone-600">Early Action</div>
                    </div>
                  </div>
                  <div className="text-xs text-stone-600 mt-3">3 items left</div>
                </div>
  
                <div className="border border-stone-200 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="w-2 h-2 rounded-full bg-gold-500 mb-2"></div>
                      <div className="font-medium text-slate-900 text-sm">Georgetown</div>
                      <div className="text-xs text-stone-600">Early Action</div>
                    </div>
                  </div>
                  <div className="text-xs text-stone-600 mt-3">2 items left</div>
                </div>
              </div>
            </div>
          </div>
  
          {/* Right Column - Context & Strategy */}
          <div className="space-y-6">
            
            {/* Timeline Health */}
            <div className="bg-slate-900 text-white rounded-lg p-6">
              <div className="text-xs font-medium uppercase tracking-wide mb-3 text-stone-400">Timeline Status</div>
              <div className="text-3xl font-bold mb-2">2 days ahead</div>
              <p className="text-sm text-stone-300 mb-4">You're tracking well. Keep this pace through ED deadline.</p>
              
              <div className="space-y-2 text-xs">
                <div className="flex items-center justify-between">
                  <span className="text-stone-400">Time committed</span>
                  <span className="font-medium">28h / 40h available</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-stone-400">Applications ready</span>
                  <span className="font-medium">65%</span>
                </div>
              </div>
            </div>
  
            {/* Strategy */}
            <div className="bg-white border border-stone-300 rounded-lg p-6">
              <div className="flex items-center gap-2 mb-4">
              <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-indigo-600 mt-0.5" />
                <div>
                  <div className="text-xs font-bold text-indigo-600 uppercase tracking-wide mb-1">Strategic Investment</div>
                  <h3 className="font-bold text-slate-900 mb-2">Spend 20 minutes on your Strategic Profile</h3>
                  <p className="text-sm text-stone-700 mb-3">
                    This improves all AI recommendations by 40% and saves you 3+ hours across research, essay drafting, and college matching.
                  </p>
                  
                  {/* Progress bar */}
                  <div className="mb-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-stone-600">Current: 6/10</span>
                      <span className="text-xs font-medium text-indigo-600">Target: 9/10</span>
                    </div>
                    <div className="h-2 bg-stone-200 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-indigo-400 to-indigo-600" style={{width: '60%'}}></div>
                    </div>
                  </div>
  
                  <button className="text-sm font-medium text-indigo-600 hover:text-indigo-700 flex items-center gap-1">
                    Invest 20 minutes
                    <ArrowRight className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </div>
              </div>
            </div>
  
  
            {/* Parent Bridge */}
            <div className="bg-white border border-stone-300 rounded-lg p-6">
              <h3 className="font-bold text-slate-900 mb-4 text-sm">Parent Bridge</h3>
              
              <button className="w-full bg-teal-600 text-white py-3 rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors mb-3">
                Request essay review
              </button>
              
              <button className="w-full border-2 border-stone-300 text-slate-900 py-3 rounded-lg text-sm font-medium hover:bg-stone-50 transition-colors">
                Share my progress
              </button>
            </div>
          </div>
        </div>
 

        

        {/* Essay Development System */}
        <div className="max-w-7xl mx-auto mb-8">
          <div className="bg-white border-2 border-indigo-500 rounded-xl p-6">
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-start gap-4">
                <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-indigo-100 flex-shrink-0">
                  <Wand2 className="w-5 h-5 text-indigo-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-slate-900 mb-1">Essay Development System</h3>
                  <p className="text-sm text-stone-700">
                    Never stare at a blank page. Our 4-step process takes you from "I don't know what to write" to polished draft.
                  </p>
                </div>
              </div>

              {/* Toggle */}
              <div className="flex items-center gap-2 bg-stone-100 rounded-lg p-1">
                <button
                  onClick={() => setEssayFlowType('personal')}
                  className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                    essayFlowType === 'personal'
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-stone-600 hover:text-slate-900'
                  }`}
                >
                  Personal Essay
                </button>
                <button
                  onClick={() => setEssayFlowType('supplemental')}
                  className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                    essayFlowType === 'supplemental'
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-stone-600 hover:text-slate-900'
                  }`}
                >
                  Supplemental
                </button>
              </div>
            </div>

            {/* Personal Essay Flow */}
            {essayFlowType === 'personal' && (
              <>
                <div className="mb-4 text-sm text-stone-700 bg-stone-50 rounded-lg p-3 border-l-2 border-indigo-500">
                  <span className="font-medium">Common App essay:</span> Start from scratch and discover your authentic story
                </div>

                <div className="grid grid-cols-4 gap-3 mb-4">
                  <div className="relative">
                    <div className="bg-gradient-to-br from-teal-50 to-teal-100 border-2 border-teal-500 rounded-lg p-4">
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-teal-600 text-white text-xs font-bold mb-3">
                        <Lightbulb className="w-4 h-4" />
                      </div>
                      <div className="text-xs font-bold text-slate-900 mb-1">Ideation</div>
                      <div className="text-xs text-stone-600 leading-relaxed">Discover 8-10 strong concepts</div>
                      <div className="mt-3 text-xs text-teal-700 font-medium">~20 min</div>
                    </div>
                    <div className="absolute -right-4 top-1/2 transform -translate-y-1/2 z-10">
                      <ArrowRight className="w-8 h-8 text-stone-400" />
                    </div>
                  </div>

                  <div className="relative">
                    <div className="bg-gradient-to-br from-azure-50 to-azure-100 border-2 border-azure-500 rounded-lg p-4">
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-azure-600 text-white text-xs font-bold mb-3">
                        <Target className="w-4 h-4" />
                      </div>
                      <div className="text-xs font-bold text-slate-900 mb-1">Topic Dev</div>
                      <div className="text-xs text-stone-600 leading-relaxed">Build detailed 650-word outline</div>
                      <div className="mt-3 text-xs text-azure-700 font-medium">~25 min</div>
                    </div>
                    <div className="absolute -right-4 top-1/2 transform -translate-y-1/2 z-10">
                      <ArrowRight className="w-8 h-8 text-stone-400" />
                    </div>
                  </div>

                  <div className="relative">
                    <div className="bg-gradient-to-br from-gold-50 to-gold-100 border-2 border-gold-500 rounded-lg p-4">
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gold-600 text-white text-xs font-bold mb-3">
                        <FileText className="w-4 h-4" />
                      </div>
                      <div className="text-xs font-bold text-slate-900 mb-1">Builder</div>
                      <div className="text-xs text-stone-600 leading-relaxed">Write section-by-section to draft</div>
                      <div className="mt-3 text-xs text-gold-700 font-medium">~60 min</div>
                    </div>
                    <div className="absolute -right-4 top-1/2 transform -translate-y-1/2 z-10">
                      <ArrowRight className="w-8 h-8 text-stone-400" />
                    </div>
                  </div>

                  <div>
                    <div className="bg-gradient-to-br from-coral-50 to-coral-100 border-2 border-coral-500 rounded-lg p-4">
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-coral-600 text-white text-xs font-bold mb-3">
                        <Sparkles className="w-4 h-4" />
                      </div>
                      <div className="text-xs font-bold text-slate-900 mb-1">Analysis</div>
                      <div className="text-xs text-stone-600 leading-relaxed">Get suggestions to polish</div>
                      <div className="mt-3 text-xs text-coral-700 font-medium">~15 min</div>
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-indigo-50 rounded-lg border-l-2 border-indigo-500">
                  <div className="text-xs font-bold text-indigo-900 mb-2">Common App essay status</div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 text-xs">
                      <CheckCircle2 className="w-3 h-3 text-teal-600" />
                      <span className="text-stone-700">Ideation complete</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      <CheckCircle2 className="w-3 h-3 text-teal-600" />
                      <span className="text-stone-700">Outline ready</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      <div className="w-3 h-3 rounded-full border-2 border-gold-500 bg-gold-100"></div>
                      <span className="text-stone-700 font-medium">Ready for Builder →</span>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Supplemental Essay Flow */}
            {essayFlowType === 'supplemental' && (
              <>
                <div className="mb-4 text-sm text-stone-700 bg-stone-50 rounded-lg p-3 border-l-2 border-indigo-500">
                  <span className="font-medium">Northwestern "Why Northwestern?":</span> Topic defined, let's develop it
                </div>

                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div className="relative">
                    <div className="bg-gradient-to-br from-teal-50 to-teal-100 border-2 border-teal-500 rounded-lg p-4">
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-teal-600 text-white text-xs font-bold mb-3">
                        <FileText className="w-4 h-4" />
                      </div>
                      <div className="text-xs font-bold text-slate-900 mb-2">Topic Given</div>
                      <div className="text-xs text-stone-700 leading-relaxed mb-2">"Why are you interested in Northwestern?"</div>
                      <div className="text-xs text-teal-800 font-medium bg-teal-100 rounded px-2 py-1 inline-block">300 words</div>
                    </div>
                    <div className="absolute -right-4 top-1/2 transform -translate-y-1/2 z-10">
                      <ArrowRight className="w-8 h-8 text-stone-400" />
                    </div>
                  </div>

                  <div className="relative">
                    <div className="bg-gradient-to-br from-azure-50 to-azure-100 border-2 border-azure-500 rounded-lg p-4">
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-azure-600 text-white text-xs font-bold mb-3">
                        <Target className="w-4 h-4" />
                      </div>
                      <div className="text-xs font-bold text-slate-900 mb-1">Topic Dev</div>
                      <div className="text-xs text-stone-600 leading-relaxed">Research + build detailed outline</div>
                      <div className="mt-3 text-xs text-azure-700 font-medium">~20 min</div>
                    </div>
                    <div className="absolute -right-4 top-1/2 transform -translate-y-1/2 z-10">
                      <ArrowRight className="w-8 h-8 text-stone-400" />
                    </div>
                  </div>

                  <div className="relative">
                    <div className="bg-gradient-to-br from-gold-50 to-gold-100 border-2 border-gold-500 rounded-lg p-4">
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gold-600 text-white text-xs font-bold mb-3">
                        <FileText className="w-4 h-4" />
                      </div>
                      <div className="text-xs font-bold text-slate-900 mb-1">Builder</div>
                      <div className="text-xs text-stone-600 leading-relaxed">Write section-by-section to draft</div>
                      <div className="mt-3 text-xs text-gold-700 font-medium">~45 min</div>
                    </div>
                    <div className="absolute -right-4 top-1/2 transform -translate-y-1/2 z-10">
                      <ArrowRight className="w-8 h-8 text-stone-400" />
                    </div>
                  </div>

                  <div>
                    <div className="bg-gradient-to-br from-coral-50 to-coral-100 border-2 border-coral-500 rounded-lg p-4">
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-coral-600 text-white text-xs font-bold mb-3">
                        <Sparkles className="w-4 h-4" />
                      </div>
                      <div className="text-xs font-bold text-slate-900 mb-1">Analysis</div>
                      <div className="text-xs text-stone-600 leading-relaxed">Get suggestions to polish</div>
                      <div className="mt-3 text-xs text-coral-700 font-medium">~10 min</div>
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-indigo-50 rounded-lg border-l-2 border-indigo-500">
                  <div className="text-xs font-bold text-indigo-900 mb-2">Northwestern supplement status</div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 text-xs">
                      <div className="w-3 h-3 rounded-full border-2 border-azure-500 bg-azure-100"></div>
                      <span className="text-stone-700 font-medium">Ready to start Topic Dev →</span>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

  
       
      </div>
    );
  }